import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  Asset,
  AssetsService,
  Documentation,
  DocumentationService,
  DocumentationType,
  I18nService,
  SettingsService,
  UiService,
  UtilService,
  NavButtonComponent,
  AssetNavButtonComponent,
  SpinnerComponent,
} from '@lp-ui-lib';
import { DocumentationItemComponent } from '../documentation-item/documentation-item';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
  selector: 'app-docu-wizard',
  templateUrl: './documentation-wizard.html',
  styleUrls: ['./documentation-wizard.scss'],
  imports: [
    NavButtonComponent,
    AssetNavButtonComponent,
    SpinnerComponent,
    NzModalModule,
    NzIconModule,
    DocumentationItemComponent,
  ],
})
export class DocumentationWizardComponent implements AfterViewInit {
  // ----- Inputs & Outputs (Signals API) -----
  assetId = input<number>(-1);
  sensorId = input<number>(-1);

  closeEvent = output<void>();
  saved = output<Documentation>();
  private service = inject(DocumentationService);
  private assets = inject(AssetsService);
  private settings = inject(SettingsService);
  private util = inject(UtilService);
  public ui = inject(UiService);
  public i18n = inject(I18nService);

  // ----- Local State (Signals) -----
  step = signal<'start' | 'add' | 'note'>('start');
  isLoading = signal(false);
  radioValue = signal(0);
  documentationError = signal(false);

  documentation = signal<Documentation>({
    documentationId: 0,
    userName: '',
    assetId: -1,
    created: new Date(),
    saved: new Date(),
    objectID: 0,
    sensorID: 0,
    documentationType: 'repair',
    note: '',
    images: [],
    files: [],
    audios: [],
  });

  // If UiService exposes signals or functions, wrap as computed
  // Adjust to `this.ui.IsDesktop` vs `this.ui.IsDesktop()` based on your UiService API.
  isDesktop = computed<boolean>(() => this.ui.IsDesktop());

  constructor() {
    // Auto-refresh when inputs change (runs after construction & on changes)
    effect(() => {
      const a = this.assetId();
      const s = this.sensorId();
      // Skip invalid/sentinel values
      if (a == null || s == null || a < 0 || s < 0) {
        return;
      }
      // Avoid writing signals in the same reactive turn as a read
      queueMicrotask(() => this.refresh(a, s));
    });
  }

  // Initial load if the inputs are already present
  ngAfterViewInit(): void {
    const a = this.assetId();
    const s = this.sensorId();
    if (a != null && s != null && a >= 0 && s >= 0) {
      this.refresh(a, s);
    }
  }

  onWizardCancel() {
    if (this.step() === 'note') {
      this.step.set('add');
    } else {
      this.closeEvent.emit(); // parent destroys the component -> next open is fresh
    }
  }

  // ----- Data Load & Actions -----

  refresh(assetId: number, sensorId: number): void {
    this.documentationError.set(false);
    if (this.isLoading()) return;

    this.isLoading.set(true);

    this.service
      .getUnfinishedDocumentation(assetId, sensorId)
      .then((d: any) => {
        this.documentation.set(d);
        if (d.documentationType) this.step.set('add');

        this.isLoading.set(false);
      })
      .catch((e) => {
        if (e?.status === 403) {
          this.documentationError.set(true);
        }
        this.isLoading.set(false);
      });
  }

  setDocumentationType(type: DocumentationType): void {
    this.documentationError.set(false);

    // Immutable update
    this.documentation.update((d) => ({
      ...d,
      documentationType: type,
      assetId: this.assetId(),
    }));

    this.service
      .updateEventDocumentation(this.documentation())
      .then(() => this.step.set('add'))
      .catch((e) => {
        if (e?.status === 403) this.documentationError.set(true);
      });
  }

  onNoteInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value; // TS cast is fine in TS
    this.documentation.update((d) => ({ ...d, note: value }));
  }

  setNote(): void {
    this.documentationError.set(false);

    this.service
      .updateEventDocumentation(this.documentation())
      .then(() => this.step.set('add'))
      .catch((e) => {
        if (e?.status === 403) this.documentationError.set(true);
      });
  }

  save(): void {
    this.documentationError.set(false);

    if (this.isLoading()) return;
    this.isLoading.set(true);

    const id = this.documentation().documentationId;

    this.service
      .finishDocumentation(id)
      .then(() => {
        this.saved.emit(this.documentation());
        this.closeEvent.emit();
        this.isLoading.set(false);

        this.refreshAllAppData();

        window.location.reload();
      })
      .catch((e) => {
        if (e?.status === 403) this.documentationError.set(true);
        this.isLoading.set(false);
      });
  }

  async refreshAllAppData(): Promise<void> {
    this.util.setLoadingStarted();

    try {
      // Kick off Promises in parallel
      const regionsPromise = this.assets.getRegions(true); // Promise<Location[]>
      const historyPromise = this.assets.getObjectHistory(this.assetId(), this.sensorId(), 6);
      const notificationsPromise = this.assets.getNotifications();
      const latestSettingsPromise = this.settings.getUserSettings(); // Promise<UserSettings>
      const sleeper = sleep(1500); // replaces RxJS timer(1500)
      // Wait for regions because getAsset depends on locations
      const regions = await regionsPromise;
      // getAsset is synchronous and requires locations: Location[]
      const asset: Asset = this.assets.getAsset(this.assetId(), regions);
      // Now wait for the rest to complete
      await Promise.all([
        historyPromise,
        notificationsPromise,
        latestSettingsPromise,
        sleeper,
        Promise.resolve(asset),
      ]);
      this.util.setLoadingFinished();
    } catch (e: any) {
      if (e?.status === 401) {
        this.util.setLoadingFinished();
        return;
      }
      console.error(e);
      this.util.setLoadingFinished(true);
    }
  }
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}
